import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { CarFront, Clock3, FileText, Loader2, LogOut, Paperclip, Phone, Plus, Search, ShieldCheck, Trash2, Truck, Upload, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { documentService } from '../services/documentService';
import { driverService } from '../services/driverService';
import { gateCheckpointService } from '../services/gateCheckpointService';

interface WarehouseOption {
  id: string;
  name: string;
}

interface GateCheckpointRow {
  id: string;
  warehouse_id: string;
  warehouse_name: string;
  vehicle_type: 'own' | 'spot';
  vehicle_plate: string;
  trailer_plate: string;
  driver_name: string;
  driver_phone: string;
  entry_at: string;
  exit_at: string | null;
  notes: string;
}

interface GateDocumentRow {
  id: string;
  file_name: string;
  file_path: string;
  document_type: string;
  document_date: string | null;
  notes: string;
  uploaded_at: string;
}

interface DocumentTargetOption {
  key: string;
  label: string;
  entityType: string;
  entityId: string;
}

interface OwnVehicleOption {
  id: string;
  trailerId?: string;
  plate: string;
  trailerPlate: string;
  driverName: string;
  driverPhone: string;
  vehicleType: string;
  hasTemporaryAssignment: boolean;
}

interface SpotDriverOption {
  id: string;
  userId?: string;
  name: string;
  phone: string;
  tcNumber?: string;
  plate: string;
  trailerPlate: string;
  vehicleType: string;
  city: string;
  invoiceTitle?: string;
}

const SPOT_INLINE_DOCUMENT_COLUMNS = [
  'tax-plate',
  'license',
  'tractor-license',
  'trailer-license',
  'src',
  'vehicle-insurance',
  'contract',
  'carrier-authority',
  'psychotechnic',
] as const;

const SPOT_DOCUMENT_LABELS: Record<(typeof SPOT_INLINE_DOCUMENT_COLUMNS)[number], string> = {
  'tax-plate': 'Vergi Levhasi',
  'license': 'Ehliyet',
  'tractor-license': 'Cekici Ruhsat',
  'trailer-license': 'Dorse Ruhsat',
  'src': 'SRC Belgesi',
  'vehicle-insurance': 'Arac Sigortasi',
  'contract': 'Tasima Sozlesmesi',
  'carrier-authority': 'Tasiyici Yetki Belgesi',
  'psychotechnic': 'Psikoteknik Belgesi',
};

const isPreviewImage = (filePath?: string | null, mimeType?: string | null) => {
  const value = String(filePath || '').toLowerCase();
  const mime = String(mimeType || '').toLowerCase();
  return mime.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(value);
};

const dedupeBy = <T,>(items: T[], getKey: (item: T) => string) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const formatDateTimeLocal = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR');
};

const toDateTimeLocalInput = (value: Date) => {
  const offset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 16);
};

const parseWarehouseScope = (value: unknown) => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeSearch = (value: string) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();
const normalizePlateSearch = (value: string) => normalizeSearch(value).replace(/\s+/g, '');

const resolveFileUrl = (rawPath: string) => {
  if (!rawPath) return '';
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
};

const normalizeUpper = (value: string) => String(value || '').toLocaleUpperCase('tr-TR');
const normalizePhoneInput = (value: string) => String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
const normalizePlate = (value: string) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
const isValidSpotPlate = (value: string) => {
  const plate = normalizePlate(value);
  return !plate || (plate.length >= 6 && plate.length <= 9 && /^\d{2}[A-ZÇĞİÖŞÜ]{1,3}\d{2,4}$/.test(plate));
};

const defaultSpotDriverForm = {
  full_name: '',
  phone: '',
  email: '',
  license_number: '',
  license_class: 'CE',
  tc_number: '',
  cekici_plaka: '',
  dorse_plaka: '',
  vehicle_type: 'Tır Tenteli',
  city: '',
  start_date: new Date().toISOString().slice(0, 10),
  invoice_title: '',
};

const getTodayDateInputValue = () => new Date().toISOString().slice(0, 10);

export function GateEntryExit() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<GateCheckpointRow[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [allowedWarehouseIds, setAllowedWarehouseIds] = useState<string[]>([]);
  const [hasGlobalScope, setHasGlobalScope] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'open' | 'closed' | 'all'>('open');
  const [startDate, setStartDate] = useState(getTodayDateInputValue());
  const [endDate, setEndDate] = useState(getTodayDateInputValue());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exitingId, setExitingId] = useState<string | null>(null);
  const [ownVehicles, setOwnVehicles] = useState<OwnVehicleOption[]>([]);
  const [spotDrivers, setSpotDrivers] = useState<SpotDriverOption[]>([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<string[]>([]);
  const [ownVehicleSearch, setOwnVehicleSearch] = useState('');
  const [spotDriverSearch, setSpotDriverSearch] = useState('');
  const [showSpotDriverModal, setShowSpotDriverModal] = useState(false);
  const [duplicateSpotDriverHint, setDuplicateSpotDriverHint] = useState<{ message: string; query: string } | null>(null);
  const [creatingSpotDriver, setCreatingSpotDriver] = useState(false);
  const [spotDriverForm, setSpotDriverForm] = useState(defaultSpotDriverForm);
  const [spotDriverDocumentsByType, setSpotDriverDocumentsByType] = useState<Record<string, Record<string, any[]>>>({});
  const [uploadingSpotDocumentKey, setUploadingSpotDocumentKey] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<{ id?: string; userId?: string; documentType?: string; name: string; url: string; isImage: boolean } | null>(null);
  const [gateDocuments, setGateDocuments] = useState<GateDocumentRow[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [activeDocumentTargetKey, setActiveDocumentTargetKey] = useState('');
  const [documentsModalTitle, setDocumentsModalTitle] = useState('');
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [documentForm, setDocumentForm] = useState({ documentType: 'other', documentDate: '', notes: '' });
  const [formData, setFormData] = useState({
    warehouseId: '',
    vehicleType: 'own' as 'own' | 'spot',
    selectedOwnVehicleId: '',
    selectedSpotDriverId: '',
    vehiclePlate: '',
    trailerPlate: '',
    driverName: '',
    driverPhone: '',
    entryAt: toDateTimeLocalInput(new Date()),
    notes: '',
  });

  const allowedWarehouses = useMemo(() => warehouses.filter((item) => allowedWarehouseIds.includes(String(item.id))), [allowedWarehouseIds, warehouses]);
  const searchableOwnVehicles = useMemo(() => ownVehicles.map((item) => ({
    ...item,
    searchText: normalizeSearch([item.plate, item.trailerPlate, item.driverName, item.driverPhone, item.vehicleType, item.hasTemporaryAssignment ? 'gecici gorevli' : ''].join(' ')),
    plateSearchText: normalizePlateSearch([item.plate, item.trailerPlate].join(' ')),
  })), [ownVehicles]);
  const searchableSpotDrivers = useMemo(() => spotDrivers.map((item) => ({
      ...item,
      searchText: normalizeSearch([item.name, item.phone, item.tcNumber, item.plate, item.trailerPlate, item.vehicleType, item.city, item.invoiceTitle].join(' ')),
      plateSearchText: normalizePlateSearch([item.plate, item.trailerPlate].join(' ')),
    })), [spotDrivers]);

  const filteredOwnVehicles = useMemo(() => {
    const query = normalizeSearch(ownVehicleSearch);
    const plateQuery = normalizePlateSearch(ownVehicleSearch);
    if (!query) return searchableOwnVehicles;
    return searchableOwnVehicles.filter((item) => item.searchText.includes(query) || (plateQuery && item.plateSearchText.includes(plateQuery)));
  }, [ownVehicleSearch, searchableOwnVehicles]);

  const MIN_SEARCH_LENGTH = 3;

  const filteredSpotDrivers = useMemo(() => {
    const query = normalizeSearch(spotDriverSearch);
    const plateQuery = normalizePlateSearch(spotDriverSearch);
    if (!query || query.length < MIN_SEARCH_LENGTH) return [];
    return searchableSpotDrivers.filter((item) => item.searchText.includes(query) || (plateQuery && item.plateSearchText.includes(plateQuery)));
  }, [spotDriverSearch, searchableSpotDrivers]);

  const openRows = records.filter((item) => !item.exit_at);
  const closedRows = records.filter((item) => Boolean(item.exit_at));
  const selectedOwnVehicle = useMemo(() => ownVehicles.find((item) => item.id === formData.selectedOwnVehicleId) || null, [ownVehicles, formData.selectedOwnVehicleId]);
  const selectedSpotDriver = useMemo(() => spotDrivers.find((item) => item.id === formData.selectedSpotDriverId) || null, [spotDrivers, formData.selectedSpotDriverId]);
  const currentDocumentTargets = useMemo<DocumentTargetOption[]>(() => {
    if (selectedSpotDriver?.userId) {
      return [{ key: `driver:${selectedSpotDriver.userId}`, label: 'Spot Sürücü Evrakları', entityType: 'driver', entityId: selectedSpotDriver.userId }];
    }
    if (selectedOwnVehicle) {
      const targets: DocumentTargetOption[] = [{ key: `vehicle:${selectedOwnVehicle.id}`, label: 'Çekici Evrakları', entityType: 'vehicle_document', entityId: selectedOwnVehicle.id }];
      if (selectedOwnVehicle.trailerId) targets.push({ key: `trailer:${selectedOwnVehicle.trailerId}`, label: 'Dorse Evrakları', entityType: 'trailer_document', entityId: selectedOwnVehicle.trailerId });
      return targets;
    }
    return [];
  }, [selectedOwnVehicle, selectedSpotDriver]);
  const activeDocumentTarget = useMemo(() => currentDocumentTargets.find((item) => item.key === activeDocumentTargetKey) || currentDocumentTargets[0] || null, [currentDocumentTargets, activeDocumentTargetKey]);

  const loadDocumentsForTarget = async (target: DocumentTargetOption) => {
    setDocumentsLoading(true);
    try {
      const response: any = await documentService.getDocuments({ entityType: target.entityType, entityId: target.entityId });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setGateDocuments(rows.map((item: any) => ({
        id: String(item.id),
        file_name: item.file_name || 'Belge',
        file_path: item.file_path || '',
        document_type: item.document_type || 'other',
        document_date: item.document_date || null,
        notes: item.notes || '',
        uploaded_at: item.uploaded_at || '',
      })));
    } catch (error) {
      console.error('Gate documents load error:', error);
      toast.error('Nizamiye belgeleri yüklenemedi');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadSpotDriverDocuments = async (userId: string) => {
    if (!userId) return;
    const response: any = await documentService.getDocuments({ entityType: 'driver', entityId: userId });
    const rows = Array.isArray(response?.data) ? response.data : [];
    const grouped = rows.reduce((acc: Record<string, any[]>, item: any) => {
      const key = String(item.document_type || 'other');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
    setSpotDriverDocumentsByType((current) => ({ ...current, [userId]: grouped }));
  };

  useEffect(() => {
    if (formData.vehicleType !== 'spot') return;
    const targets = filteredSpotDrivers.slice(0, 20).map((driver) => driver.userId).filter(Boolean) as string[];
    const missing = targets.filter((userId) => !spotDriverDocumentsByType[userId]);
    if (missing.length === 0) return;
    void Promise.all(missing.map((userId) => loadSpotDriverDocuments(userId).catch(() => null)));
  }, [formData.vehicleType, filteredSpotDrivers, spotDriverDocumentsByType]);

  const handleSpotDriverDocumentUpload = async (driver: SpotDriverOption, documentType: (typeof SPOT_INLINE_DOCUMENT_COLUMNS)[number], event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !driver.userId) return;
    try {
      setUploadingSpotDocumentKey(`${driver.id}:${documentType}`);
      const form = new FormData();
      form.append('file', file);
      form.append('entityType', 'driver');
      form.append('entityId', driver.userId);
      form.append('documentType', documentType);
      form.append('name', file.name);
      await documentService.uploadDocument(form);
      await loadSpotDriverDocuments(driver.userId);
      toast.success(`${SPOT_DOCUMENT_LABELS[documentType]} yüklendi`);
    } catch (error: any) {
      console.error('Spot driver document upload failed:', error);
      toast.error(error?.message || 'Belge yüklenemedi');
    } finally {
      setUploadingSpotDocumentKey(null);
      event.target.value = '';
    }
  };

  const renderSpotInlineDocumentCell = (driver: SpotDriverOption, documentType: (typeof SPOT_INLINE_DOCUMENT_COLUMNS)[number]) => {
    const userId = driver.userId || '';
    const docs = userId ? (spotDriverDocumentsByType[userId]?.[documentType] || []) : [];
    const latest = docs[0] || null;
    const uploadKey = `${driver.id}:${documentType}`;
    return (
      <div className="flex min-w-[120px] items-center gap-2">
        {latest ? (
          <button
            type="button"
            onClick={() => setPreviewDocument({ id: String(latest.id || ''), userId, documentType, name: latest.file_name || SPOT_DOCUMENT_LABELS[documentType], url: resolveFileUrl(latest.file_path || ''), isImage: isPreviewImage(latest.file_path, latest.mime_type) })}
            className="rounded-lg border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#1a1a1a]"
          >
            Gor
          </button>
        ) : <span className="text-[11px] text-slate-400">Yok</span>}
        <label className={`inline-flex cursor-pointer items-center gap-1 rounded-lg border border-amber-300 px-2 py-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-50 dark:border-amber-500/30 dark:text-amber-200 dark:hover:bg-amber-500/10 ${!userId ? 'pointer-events-none opacity-50' : ''}`}>
          {uploadingSpotDocumentKey === uploadKey ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          Yukle
          <input type="file" accept=".pdf,image/*" disabled={!userId || uploadingSpotDocumentKey === uploadKey} onChange={(event) => void handleSpotDriverDocumentUpload(driver, documentType, event)} className="hidden" />
        </label>
      </div>
    );
  };

  const handleDeletePreviewDocument = async () => {
    if (!previewDocument?.id) return;
    try {
      setDeletingDocumentId(previewDocument.id);
      await documentService.deleteDocument(previewDocument.id);
      if (previewDocument.userId) {
        await loadSpotDriverDocuments(previewDocument.userId);
      }
      setPreviewDocument(null);
      toast.success('Belge silindi');
    } catch (error: any) {
      console.error('Preview document delete failed:', error);
      toast.error(error?.message || 'Belge silinemedi');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const openDocumentsModal = (targets: DocumentTargetOption[], title: string) => {
    if (targets.length === 0) {
      toast.error('Bu kayıt için evrak hedefi bulunamadı');
      return;
    }
    setDocumentsModalTitle(title);
    setActiveDocumentTargetKey(targets[0].key);
    setShowDocumentsModal(true);
  };

  const applyOwnVehicle = (vehicle: OwnVehicleOption) => {
    setFormData((current) => ({
      ...current,
      vehicleType: 'own',
      selectedOwnVehicleId: vehicle.id,
      selectedSpotDriverId: '',
      vehiclePlate: vehicle.plate,
      trailerPlate: vehicle.trailerPlate,
      driverName: vehicle.driverName,
      driverPhone: vehicle.driverPhone,
    }));
  };

  const applySpotDriver = (driver: SpotDriverOption) => {
    setFormData((current) => ({
      ...current,
      vehicleType: 'spot',
      selectedOwnVehicleId: '',
      selectedSpotDriverId: driver.id,
      vehiclePlate: driver.plate,
      trailerPlate: driver.trailerPlate,
      driverName: driver.name,
      driverPhone: driver.phone,
    }));
  };

  const loadFormOptions = async () => {
    const response: any = await gateCheckpointService.getFormOptions();
    const data = response?.data || {};
    const warehouseOptions = Array.isArray(data.warehouses) ? data.warehouses.map((item: any) => ({ id: String(item.id), name: item.name || `Depo ${item.id}` })) : [];
    const warehouseIds = Array.isArray(data.allowedWarehouseIds) ? data.allowedWarehouseIds.map(String) : parseWarehouseScope(data.allowedWarehouseIds);
    setHasGlobalScope(Boolean(data.hasGlobalScope));
    setAllowedWarehouseIds(warehouseIds);
    setWarehouses(warehouseOptions);
    setOwnVehicles(dedupeBy(Array.isArray(data.ownVehicles) ? data.ownVehicles : [], (item: any) => String(item.id || normalizePlateSearch(item.plate || ''))));
    setSpotDrivers(dedupeBy(Array.isArray(data.spotDrivers) ? data.spotDrivers : [], (item: any) => String(item.id || item.userId || normalizePlateSearch(item.plate || ''))));
    setVehicleTypeOptions(Array.isArray(data.vehicleTypeOptions) ? data.vehicleTypeOptions.map((item: string) => normalizeUpper(item)) : []);
    const defaultWarehouseId = warehouseOptions[0]?.id || warehouseIds[0] || '';
    setSelectedWarehouseId((current) => current || defaultWarehouseId);
    setFormData((current) => ({ ...current, warehouseId: current.warehouseId || defaultWarehouseId }));
  };

  const loadRecords = async (warehouseId?: string, status?: 'open' | 'closed' | 'all', rangeStartDate?: string, rangeEndDate?: string) => {
    const response = await gateCheckpointService.getAll({ warehouseId: warehouseId || '', status: status || statusFilter, startDate: rangeStartDate || startDate, endDate: rangeEndDate || endDate });
    const nextRows = Array.isArray(response?.data) ? response.data : [];
    setRecords(nextRows.map((item: any) => ({
      id: String(item.id),
      warehouse_id: String(item.warehouse_id),
      warehouse_name: item.warehouse_name || '-',
      vehicle_type: item.vehicle_type === 'spot' ? 'spot' : 'own',
      vehicle_plate: item.vehicle_plate || '-',
      trailer_plate: item.trailer_plate || '',
      driver_name: item.driver_name || '',
      driver_phone: item.driver_phone || '',
      entry_at: item.entry_at,
      exit_at: item.exit_at || null,
      notes: item.notes || '',
    })));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await loadFormOptions();
      await loadRecords(selectedWarehouseId, statusFilter, startDate, endDate);
    } catch (error) {
      console.error('Gate entry exit load error:', error);
      toast.error('Nizamiye verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      void loadRecords(selectedWarehouseId, statusFilter, startDate, endDate).catch((error) => {
        console.error('Gate checkpoints filter load error:', error);
        toast.error('Kayıtlar filtrelenemedi');
      });
    }
  }, [selectedWarehouseId, statusFilter, startDate, endDate]);

  useEffect(() => {
    if (!showDocumentsModal || !activeDocumentTarget) {
      setGateDocuments([]);
      return;
    }
    void loadDocumentsForTarget(activeDocumentTarget);
  }, [showDocumentsModal, activeDocumentTarget]);

  useEffect(() => {
    if (currentDocumentTargets.length === 0) {
      setActiveDocumentTargetKey('');
      return;
    }
    if (!currentDocumentTargets.some((item) => item.key === activeDocumentTargetKey)) {
      setActiveDocumentTargetKey(currentDocumentTargets[0].key);
    }
  }, [currentDocumentTargets, activeDocumentTargetKey]);

  const handleCreate = async () => {
    if (!formData.warehouseId || !formData.vehiclePlate.trim() || !formData.entryAt) {
      toast.error('Depo, araç ve giriş tarih saat zorunludur');
      return;
    }
    setSaving(true);
    try {
      const effectiveWarehouseId = selectedWarehouseId || formData.warehouseId;
      const response: any = await gateCheckpointService.create({
        warehouseId: formData.warehouseId,
        vehicleType: formData.vehicleType,
        vehiclePlate: formData.vehiclePlate,
        trailerPlate: formData.trailerPlate,
        driverName: formData.driverName,
        driverPhone: formData.driverPhone,
        entryAt: formData.entryAt,
        notes: formData.notes,
      });
      toast.success('Nizamiye giriş kaydı oluşturuldu');
      if (!selectedWarehouseId && effectiveWarehouseId) {
        setSelectedWarehouseId(effectiveWarehouseId);
      }
      setStatusFilter('open');
      setFormData((current) => ({
        ...current,
        selectedOwnVehicleId: '',
        selectedSpotDriverId: '',
        vehiclePlate: '',
        trailerPlate: '',
        driverName: '',
        driverPhone: '',
        entryAt: toDateTimeLocalInput(new Date()),
        notes: '',
      }));
      setRecords((current) => [{
        id: `temp-${Date.now()}`,
        warehouse_id: effectiveWarehouseId,
        warehouse_name: allowedWarehouses.find((item) => item.id === effectiveWarehouseId)?.name || warehouses.find((item) => item.id === effectiveWarehouseId)?.name || '-',
        vehicle_type: formData.vehicleType,
        vehicle_plate: formData.vehiclePlate,
        trailer_plate: formData.trailerPlate,
        driver_name: formData.driverName,
        driver_phone: formData.driverPhone,
        entry_at: formData.entryAt,
        exit_at: null,
        notes: formData.notes,
      }, ...current]);
      await loadRecords(effectiveWarehouseId, 'open', startDate, endDate);
    } catch (error: any) {
      console.error('Create gate checkpoint error:', error);
      toast.error(error?.message || 'Giriş kaydı oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !activeDocumentTarget) return;
    try {
      setUploadingDocument(true);
      const payload = new FormData();
      payload.append('file', file);
      payload.append('entityType', activeDocumentTarget.entityType);
      payload.append('entityId', activeDocumentTarget.entityId);
      payload.append('documentType', documentForm.documentType || 'other');
      payload.append('name', file.name);
      if (documentForm.documentDate) payload.append('documentDate', documentForm.documentDate);
      if (documentForm.notes) payload.append('notes', documentForm.notes);
      await documentService.uploadDocument(payload);
      toast.success('Belge yüklendi');
      setDocumentForm((current) => ({ ...current, documentDate: '', notes: '' }));
      await loadDocumentsForTarget(activeDocumentTarget);
    } catch (error: any) {
      console.error('Gate document upload failed:', error);
      toast.error(error?.message || 'Belge yüklenemedi');
    } finally {
      setUploadingDocument(false);
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    setDeletingDocumentId(documentId);
    try {
      await documentService.deleteDocument(documentId);
      setGateDocuments((current) => current.filter((item) => item.id !== documentId));
      toast.success('Belge silindi');
    } catch (error: any) {
      console.error('Gate document delete failed:', error);
      toast.error(error?.message || 'Belge silinemedi');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const handleExit = async (row: GateCheckpointRow) => {
    setExitingId(row.id);
    try {
      await gateCheckpointService.exit(row.id, { exitAt: new Date().toISOString() });
      toast.success('Çıkış tarih ve saati kaydedildi');
      await loadRecords(selectedWarehouseId, statusFilter, startDate, endDate);
    } catch (error: any) {
      console.error('Exit gate checkpoint error:', error);
      toast.error(error?.message || 'Çıkış kaydı yapılamadı');
    } finally {
      setExitingId(null);
    }
  };

  const handleCreateSpotDriver = async () => {
    if (!spotDriverForm.full_name.trim() || !spotDriverForm.phone.trim() || !spotDriverForm.license_number.trim() || !spotDriverForm.license_class.trim() || !spotDriverForm.start_date.trim()) {
      toast.error('Ad soyad, telefon, ehliyet numarası, ehliyet sınıfı ve başlangıç tarihi zorunludur');
      return;
    }
    if (!isValidSpotPlate(spotDriverForm.cekici_plaka)) return toast.error('Cekici plaka formati gecersiz');
    if (String(spotDriverForm.dorse_plaka || '').trim() && !isValidSpotPlate(spotDriverForm.dorse_plaka)) return toast.error('Dorse plaka formati gecersiz');
    setCreatingSpotDriver(true);
    try {
      const response: any = await driverService.createDriver({ ...spotDriverForm, full_name: normalizeUpper(spotDriverForm.full_name), phone: normalizePhoneInput(spotDriverForm.phone), cekici_plaka: normalizePlate(spotDriverForm.cekici_plaka), dorse_plaka: normalizePlate(spotDriverForm.dorse_plaka), vehicle_type: normalizeUpper(spotDriverForm.vehicle_type), city: normalizeUpper(spotDriverForm.city), invoice_title: normalizeUpper(spotDriverForm.invoice_title), driver_type: 'external', status: 'available' });
      toast.success(response?.message || 'Spot sürücü oluşturuldu');
      setShowSpotDriverModal(false);
      setSpotDriverForm(defaultSpotDriverForm);
      await loadFormOptions();
    } catch (error: any) {
      console.error('Create spot driver failed:', error);
      const message = String(error?.message || 'Spot sürücü oluşturulamadı');
      const duplicateQuery = String(spotDriverForm.tc_number || spotDriverForm.phone || spotDriverForm.cekici_plaka || spotDriverForm.full_name || '').trim();
      if (duplicateQuery && /zaten kayitli|zaten kayıtlı|ayni telefon|aynı telefon|e-posta|benzersiz/i.test(message)) {
        setDuplicateSpotDriverHint({ message, query: duplicateQuery });
      } else {
        toast.error(message);
      }
    } finally {
      setCreatingSpotDriver(false);
    }
  };

  return (
    <div className="space-y-6 text-slate-900 dark:text-slate-100">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
              <ShieldCheck className="h-3.5 w-3.5" /> Nizamiye Operasyon Paneli
            </div>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Nizamiye Giriş / Çıkış</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-400">Yetkili olduğunuz depolarda özmal ve spot araçların giriş ile çıkış tarih/saatlerini profesyonel bir akışla yönetin. Arama ile araç seçebilir, spot sürücü kaydını bu ekrandan hızlıca oluşturabilirsiniz.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-[#181818]">
              <div className="text-xs text-slate-500 dark:text-slate-400">Yetkili Depo</div>
              <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{hasGlobalScope ? 'Tümü' : allowedWarehouses.length}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-[#181818]">
              <div className="text-xs text-slate-500 dark:text-slate-400">Aktif Giriş</div>
              <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{openRows.length}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-[#181818]">
              <div className="text-xs text-slate-500 dark:text-slate-400">Bugün Çıkış</div>
              <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{closedRows.length}</div>
            </div>
          </div>
        </div>
      </div>

      {allowedWarehouses.length === 0 && !loading ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          Bu ekran için size Tracking Yükleme Noktası Yetkisi tanımlanmamış. Yetki tanımlandıktan sonra giriş/çıkış kaydı yapabilirsiniz.
        </div>
      ) : null}

      <div className="space-y-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Yeni Giriş Kaydı</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Özmal ve spot sürücüler aşağıdaki tabloda. Buradan arayıp seçin, evrakları da aynı tabloda yönetin.</p>
            </div>
            {formData.vehicleType === 'spot' ? (
              <button type="button" onClick={() => setShowSpotDriverModal(true)} className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-200 px-3 py-2.5 text-sm font-semibold text-slate-900 hover:bg-amber-300 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-200 dark:hover:bg-orange-500/20">
                <UserPlus className="h-4 w-4" /> Yeni Spot Sürücü
              </button>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-[#181818]">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Depo</label>
                <select value={formData.warehouseId} onChange={(e) => setFormData((current) => ({ ...current, warehouseId: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white">
                  <option value="">Depo seçin</option>
                  {allowedWarehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setFormData((current) => ({ ...current, vehicleType: 'own', selectedSpotDriverId: '' }))} className={`rounded-2xl border px-4 py-4 text-left ${formData.vehicleType === 'own' ? 'border-blue-500 bg-blue-50 text-blue-900 dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-100' : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-200'}`}>
                  <CarFront className="h-5 w-5" />
                  <div className="mt-2 font-semibold">Özmal</div>
                </button>
                <button type="button" onClick={() => setFormData((current) => ({ ...current, vehicleType: 'spot', selectedOwnVehicleId: '' }))} className={`rounded-2xl border px-4 py-4 text-left ${formData.vehicleType === 'spot' ? 'border-violet-500 bg-violet-50 text-violet-900 dark:border-violet-400 dark:bg-violet-500/10 dark:text-violet-100' : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-[#111111] dark:text-slate-200'}`}>
                  <Truck className="h-5 w-5" />
                  <div className="mt-2 font-semibold">Spot</div>
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Çekici Plaka</label>
                  <input type="text" value={formData.vehiclePlate} onChange={(e) => setFormData((current) => ({ ...current, vehiclePlate: e.target.value.toLocaleUpperCase('tr-TR') }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white" placeholder="34 ABC 123" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Dorse Plaka</label>
                  <input type="text" value={formData.trailerPlate} onChange={(e) => setFormData((current) => ({ ...current, trailerPlate: e.target.value.toLocaleUpperCase('tr-TR') }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white" placeholder="Opsiyonel" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Sürücü Adı</label>
                  <input type="text" value={formData.driverName} onChange={(e) => setFormData((current) => ({ ...current, driverName: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white" placeholder="Sürücü adı" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Sürücü Telefon</label>
                  <input type="text" value={formData.driverPhone} onChange={(e) => setFormData((current) => ({ ...current, driverPhone: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white" placeholder="Telefon" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Giriş Tarih Saat</label>
                  <input type="datetime-local" value={formData.entryAt} onChange={(e) => setFormData((current) => ({ ...current, entryAt: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Not</label>
                  <input type="text" value={formData.notes} onChange={(e) => setFormData((current) => ({ ...current, notes: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white" placeholder="Opsiyonel açıklama" />
                </div>
              </div>

              <button type="button" onClick={() => void handleCreate()} disabled={saving || allowedWarehouses.length === 0} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-emerald-600 dark:hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {saving ? 'Kaydediliyor...' : 'Nizamiye Girişini Kaydet'}
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-[#181818]">
                <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{formData.vehicleType === 'spot' ? 'Spot Sürücü Tablosu' : 'Özmal Araç Tablosu'}</h3>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Arama burada çalışır. Satırdan seçebilir ve evrak işlemini açabilirsiniz.</p>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">Toplam: {formData.vehicleType === 'own' ? ownVehicles.length : spotDrivers.length} kayıt</div>
              </div>
              <div className="relative mb-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input value={formData.vehicleType === 'own' ? ownVehicleSearch : spotDriverSearch} onChange={(e) => formData.vehicleType === 'own' ? setOwnVehicleSearch(e.target.value) : setSpotDriverSearch(e.target.value)} placeholder={formData.vehicleType === 'own' ? '52 AFA 767, sürücü, dorse, telefon, tip' : 'En az 3 karakter ile ara (plaka, isim, telefon, şehir...)'} className="w-full rounded-xl border border-slate-300 bg-white py-3 pr-3 pl-9 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#111111] dark:text-white" />
              </div>
              <div className="max-h-[420px] overflow-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-[#111111]">
                {formData.vehicleType === 'own' ? (
                  <table className="min-w-full text-left text-sm">
                    <thead className="sticky top-0 bg-slate-100 dark:bg-[#181818]">
                      <tr className="text-slate-600 dark:text-slate-300">
                        <th className="px-3 py-2 font-medium text-right">Seç</th>
                        <th className="px-3 py-2 font-medium">Çekici</th>
                        <th className="px-3 py-2 font-medium">Dorse</th>
                        <th className="px-3 py-2 font-medium">Sürücü</th>
                        <th className="px-3 py-2 font-medium">Telefon</th>
                        <th className="px-3 py-2 font-medium">Tip</th>
                        <th className="px-3 py-2 font-medium">Durum</th>
                        <th className="px-3 py-2 font-medium">Evrak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOwnVehicles.map((vehicle) => (
                        <tr key={vehicle.id} className={`border-t border-slate-100 dark:border-slate-800 ${formData.selectedOwnVehicleId === vehicle.id ? 'bg-blue-50 dark:bg-blue-500/10' : ''}`}>
                          <td className="px-3 py-2 text-right"><button type="button" onClick={() => applyOwnVehicle(vehicle)} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">Seç</button></td>
                          <td className="px-3 py-2 font-semibold text-slate-900 dark:text-white">{vehicle.plate}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{vehicle.trailerPlate || '-'}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{vehicle.driverName || '-'}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{vehicle.driverPhone || '-'}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{vehicle.vehicleType || '-'}</td>
                          <td className="px-3 py-2">{vehicle.hasTemporaryAssignment ? <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">Geçici Görevli</span> : <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">Uygun</span>}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              <button type="button" onClick={() => openDocumentsModal([{ key: `vehicle:${vehicle.id}`, label: 'Çekici Evrakları', entityType: 'vehicle_document', entityId: vehicle.id }], `${vehicle.plate} Çekici Evrakları`)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#1a1a1a]">Çekici</button>
                              {vehicle.trailerId ? <button type="button" onClick={() => openDocumentsModal([{ key: `trailer:${vehicle.trailerId}`, label: 'Dorse Evrakları', entityType: 'trailer_document', entityId: vehicle.trailerId }], `${vehicle.trailerPlate || '-'} Dorse Evrakları`)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#1a1a1a]">Dorse</button> : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredOwnVehicles.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">Eşleşen özmal araç bulunamadı.</td></tr> : null}
                    </tbody>
                  </table>
                ) : (
                  <table className="min-w-full text-left text-sm">
                    <thead className="sticky top-0 bg-slate-100 dark:bg-[#181818]">
                      <tr className="text-slate-600 dark:text-slate-300">
                        <th className="px-3 py-2 font-medium text-right">Seç</th>
                        <th className="px-3 py-2 font-medium">Sürücü</th>
                        <th className="px-3 py-2 font-medium">Telefon</th>
                        <th className="px-3 py-2 font-medium">Çekici</th>
                        <th className="px-3 py-2 font-medium">Dorse</th>
                        <th className="px-3 py-2 font-medium">Tip</th>
                        <th className="px-3 py-2 font-medium">Şehir</th>
                        {SPOT_INLINE_DOCUMENT_COLUMNS.map((documentType) => (
                          <th key={documentType} className="px-3 py-2 font-medium whitespace-nowrap">{SPOT_DOCUMENT_LABELS[documentType]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSpotDrivers.map((driver) => (
                        <tr key={driver.id} className={`border-t border-slate-100 dark:border-slate-800 ${formData.selectedSpotDriverId === driver.id ? 'bg-violet-50 dark:bg-violet-500/10' : ''}`}>
                          <td className="px-3 py-2 text-right"><button type="button" onClick={() => applySpotDriver(driver)} className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">Seç</button></td>
                          <td className="px-3 py-2 font-semibold text-slate-900 dark:text-white">{driver.name}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{driver.phone || '-'}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{driver.plate || '-'}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{driver.trailerPlate || '-'}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{driver.vehicleType || '-'}</td>
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{driver.city || '-'}</td>
                          {SPOT_INLINE_DOCUMENT_COLUMNS.map((documentType) => (
                            <td key={documentType} className="px-3 py-2">{renderSpotInlineDocumentCell(driver, documentType)}</td>
                          ))}
                        </tr>
                      ))}
                      {filteredSpotDrivers.length === 0 ? <tr><td colSpan={7 + SPOT_INLINE_DOCUMENT_COLUMNS.length} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">{!spotDriverSearch.trim() ? 'Arama yapmak için en az 3 karakter girin.' : spotDriverSearch.trim().length < MIN_SEARCH_LENGTH ? 'Arama için en az 3 karakter girin.' : 'Eşleşen spot sürücü bulunamadı.'}</td></tr> : null}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
            <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Aktif Nizamiye Kayıtları</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Depoya giriş yapmış ve henüz çıkışı alınmamış araçlar.</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <select value={selectedWarehouseId} onChange={(e) => setSelectedWarehouseId(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white">
                  <option value="">Tüm yetkili depolar</option>
                  {allowedWarehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
                </select>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'open' | 'closed' | 'all')} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white">
                  <option value="open">Sadece Açıklar</option>
                  <option value="closed">Sadece Çıkanlar</option>
                  <option value="all">Tüm Kayıtlar</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-600 dark:text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...</div>
            ) : openRows.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">Açık giriş kaydı bulunmuyor.</div>
            ) : (
              <div className="space-y-3">
                {openRows.map((row) => (
                  <div key={row.id} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                      <div className="min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${row.vehicle_type === 'spot' ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-200' : 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-200'}`}>{row.vehicle_type === 'spot' ? 'Spot' : 'Özmal'}</span>
                          <span className="text-lg font-bold text-slate-900 dark:text-white">{row.vehicle_plate}</span>
                          {row.trailer_plate ? <span className="text-sm text-slate-600 dark:text-slate-300">/ {row.trailer_plate}</span> : null}
                        </div>
                        <div className="text-sm text-slate-700 dark:text-slate-300">{row.warehouse_name}</div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-300">
                          <span>{row.driver_name || '-'}</span>
                          <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {row.driver_phone || '-'}</span>
                        </div>
                        <div className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-200"><Clock3 className="h-4 w-4" /> Giriş: {formatDateTimeLocal(row.entry_at)}</div>
                        {row.notes ? <div className="text-sm text-slate-600 dark:text-slate-300">Not: {row.notes}</div> : null}
                      </div>
                      <button type="button" onClick={() => void handleExit(row)} disabled={exitingId === row.id} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60">
                        {exitingId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                        {exitingId === row.id ? 'Çıkış Kaydediliyor...' : 'Çıkış Yap'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#111111]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Çıkış Kaydı Olanlar</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Çıkışı tamamlanmış araç hareketleri.</p>
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{closedRows.length} kayıt</div>
            </div>
            {loading ? null : closedRows.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">Çıkış kaydı olan araç bulunmuyor.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-400">
                      <th className="px-3 py-2 font-medium">Depo</th>
                      <th className="px-3 py-2 font-medium">Tip</th>
                      <th className="px-3 py-2 font-medium">Plaka</th>
                      <th className="px-3 py-2 font-medium">Sürücü</th>
                      <th className="px-3 py-2 font-medium">Giriş</th>
                      <th className="px-3 py-2 font-medium">Çıkış</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedRows.map((row) => (
                      <tr key={row.id} className="border-b border-slate-100 text-slate-800 dark:border-slate-800 dark:text-slate-200">
                        <td className="px-3 py-3">{row.warehouse_name}</td>
                        <td className="px-3 py-3">{row.vehicle_type === 'spot' ? 'Spot' : 'Özmal'}</td>
                        <td className="px-3 py-3 font-medium">{row.vehicle_plate}</td>
                        <td className="px-3 py-3">{row.driver_name || '-'}</td>
                        <td className="px-3 py-3">{formatDateTimeLocal(row.entry_at)}</td>
                        <td className="px-3 py-3">{formatDateTimeLocal(row.exit_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showSpotDriverModal ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl dark:bg-[#111111]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Yeni Spot Sürücü</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Spot sürücüyü hızlıca oluşturup aynı ekranda seçebilirsiniz.</p>
              </div>
              <button type="button" onClick={() => setShowSpotDriverModal(false)} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-[#1a1a1a]"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-2">
              <input value={spotDriverForm.full_name} onChange={(e) => setSpotDriverForm((current) => ({ ...current, full_name: normalizeUpper(e.target.value) }))} placeholder="Ad Soyad" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.phone} onChange={(e) => setSpotDriverForm((current) => ({ ...current, phone: normalizePhoneInput(e.target.value) }))} placeholder="Telefon" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.email} onChange={(e) => setSpotDriverForm((current) => ({ ...current, email: e.target.value }))} placeholder="E-posta" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.tc_number} onChange={(e) => setSpotDriverForm((current) => ({ ...current, tc_number: e.target.value }))} placeholder="T.C. No" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.license_number} onChange={(e) => setSpotDriverForm((current) => ({ ...current, license_number: e.target.value }))} placeholder="Ehliyet Numarası" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.license_class} onChange={(e) => setSpotDriverForm((current) => ({ ...current, license_class: e.target.value }))} placeholder="Ehliyet Sınıfı" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.cekici_plaka} onChange={(e) => setSpotDriverForm((current) => ({ ...current, cekici_plaka: normalizePlate(e.target.value) }))} placeholder="Çekici Plaka" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.dorse_plaka} onChange={(e) => setSpotDriverForm((current) => ({ ...current, dorse_plaka: normalizePlate(e.target.value) }))} placeholder="Dorse Plaka" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <select value={spotDriverForm.vehicle_type} onChange={(e) => setSpotDriverForm((current) => ({ ...current, vehicle_type: e.target.value }))} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white">
                <option value="">Araç tipi seçin</option>
                {vehicleTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <input value={spotDriverForm.city} onChange={(e) => setSpotDriverForm((current) => ({ ...current, city: normalizeUpper(e.target.value) }))} placeholder="Şehir" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input type="date" value={spotDriverForm.start_date} onChange={(e) => setSpotDriverForm((current) => ({ ...current, start_date: e.target.value }))} className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
              <input value={spotDriverForm.invoice_title} onChange={(e) => setSpotDriverForm((current) => ({ ...current, invoice_title: normalizeUpper(e.target.value) }))} placeholder="Fatura Ünvanı" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
            </div>
            {duplicateSpotDriverHint ? (
              <div className="mx-6 mb-2 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-semibold">{duplicateSpotDriverHint.message}</div>
                <div className="mt-3 flex gap-3">
                  <button type="button" onClick={() => { setShowSpotDriverModal(false); setDuplicateSpotDriverHint(null); navigate(`/approved-drivers?q=${encodeURIComponent(duplicateSpotDriverHint.query)}`); }} className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700">Kayda Git</button>
                  <button type="button" onClick={() => setDuplicateSpotDriverHint(null)} className="rounded-lg border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100">Kapat</button>
                </div>
              </div>
            ) : null}
            <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-800">
              <button type="button" onClick={() => setShowSpotDriverModal(false)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#1a1a1a]">Vazgeç</button>
              <button type="button" onClick={() => void handleCreateSpotDriver()} disabled={creatingSpotDriver} className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60">
                {creatingSpotDriver ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                {creatingSpotDriver ? 'Oluşturuluyor...' : 'Spot Sürücü Oluştur'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showDocumentsModal ? (
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-5xl rounded-3xl bg-white shadow-2xl dark:bg-[#111111]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">{documentsModalTitle || 'Evraklar'}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Seçili kayıt için mevcut evrakları görüntüleyin ve yeni evrak yükleyin.</p>
              </div>
              <button type="button" onClick={() => setShowDocumentsModal(false)} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-[#1a1a1a]"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4 p-6">
              {currentDocumentTargets.length > 1 ? (
                <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-[#181818]">
                  {currentDocumentTargets.map((target) => <button key={target.key} type="button" onClick={() => setActiveDocumentTargetKey(target.key)} className={`rounded-xl px-4 py-2 text-sm font-semibold ${activeDocumentTarget?.key === target.key ? 'bg-white text-slate-900 shadow-sm dark:bg-[#111111] dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>{target.label}</button>)}
                </div>
              ) : null}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_180px_minmax(0,1fr)_220px] md:items-end">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Belge Türü</label>
                  <select value={documentForm.documentType} onChange={(e) => setDocumentForm((current) => ({ ...current, documentType: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white">
                    <option value="other">Diğer</option>
                    <option value="license">Ehliyet</option>
                    <option value="tractor-license">Çekici Ruhsat</option>
                    <option value="trailer-license">Dorse Ruhsat</option>
                    <option value="vehicle-insurance">Araç Sigortası</option>
                    <option value="src">SRC</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Belge Tarihi</label>
                  <input type="date" value={documentForm.documentDate} onChange={(e) => setDocumentForm((current) => ({ ...current, documentDate: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Not</label>
                  <input type="text" value={documentForm.notes} onChange={(e) => setDocumentForm((current) => ({ ...current, notes: e.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-[#181818] dark:text-white" placeholder="Belge açıklaması" />
                </div>
                <label className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold ${activeDocumentTarget ? 'cursor-pointer bg-emerald-600 text-white hover:bg-emerald-700' : 'cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                  {uploadingDocument ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploadingDocument ? 'Yükleniyor...' : 'Belge Seç ve Yükle'}
                  <input type="file" accept=".pdf,image/*" disabled={!activeDocumentTarget || uploadingDocument} onChange={(e) => void handleUploadDocument(e)} className="hidden" />
                </label>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-[#111111]">
                <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 dark:border-slate-800 dark:text-slate-100"><Paperclip className="h-4 w-4" /> Evrak Listesi</div>
                {documentsLoading ? <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-600 dark:text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...</div> : gateDocuments.length === 0 ? <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">Bu hedef için yüklenmiş belge yok.</div> : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {gateDocuments.map((doc) => (
                      <div key={doc.id} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
                        <div className="min-w-0">
                          <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white"><FileText className="h-4 w-4" /> <span className="truncate">{doc.file_name}</span></div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{doc.document_type} {doc.document_date ? `• ${doc.document_date}` : ''} {doc.notes ? `• ${doc.notes}` : ''}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={resolveFileUrl(doc.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-[#1a1a1a]">Görüntüle</a>
                          <button type="button" onClick={() => void handleDeleteDocument(doc.id)} disabled={deletingDocumentId === doc.id} className="inline-flex items-center gap-2 rounded-xl border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60 dark:border-rose-500/30 dark:text-rose-200 dark:hover:bg-rose-500/10">{deletingDocumentId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Sil</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {previewDocument ? (
        <div className="fixed inset-0 z-[97] flex items-center justify-center bg-black/70 p-4" onClick={() => setPreviewDocument(null)}>
          <div className="w-full max-w-5xl rounded-3xl bg-white shadow-2xl dark:bg-[#111111]" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
              <div className="truncate text-lg font-bold text-slate-900 dark:text-white">{previewDocument.name}</div>
              <div className="flex items-center gap-2">
                <a href={previewDocument.url} download={previewDocument.name} className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-200 dark:hover:bg-emerald-500/10">Indir</a>
                {previewDocument.id ? <button type="button" onClick={() => void handleDeletePreviewDocument()} disabled={deletingDocumentId === previewDocument.id} className="inline-flex items-center gap-2 rounded-xl border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60 dark:border-rose-500/30 dark:text-rose-200 dark:hover:bg-rose-500/10">{deletingDocumentId === previewDocument.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Sil</button> : null}
                <button type="button" onClick={() => setPreviewDocument(null)} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-[#1a1a1a]"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="flex max-h-[80vh] items-center justify-center p-6">
              {previewDocument.isImage ? (
                <img src={previewDocument.url} alt={previewDocument.name} className="max-h-[72vh] max-w-full rounded-lg object-contain" />
              ) : (
                <embed src={previewDocument.url} title={previewDocument.name} type="application/pdf" className="h-[72vh] w-full rounded-lg bg-white" />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
